import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle } from 'react-icons/fi';

import { Flex, Loader, Text } from '@highlight-cp/core-ui';

interface AutoSaveStatusProps {
  className?: string;
  isSaving: boolean;
  showTimestamp?: boolean;
}

/**
 * The status sits beside a row of figures wherever it appears — a header, a control bar — so it is
 * set in the small, medium-weight type those read in rather than on the body scale it would take
 * on a line of its own (EPD-1562).
 */
const AutoSaveStatus = ({ isSaving, className, showTimestamp = true }: AutoSaveStatusProps) => {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!isSaving && saved) {
      setLastSavedAt(new Date());
    } else if (isSaving) {
      setSaved(true);
    }
  }, [isSaving, saved]);

  if (!isSaving && !lastSavedAt) {
    return null;
  }

  return (
    <Flex align="center" className={className} gap={4}>
      {lastSavedAt && showTimestamp && (
        <Text c="dimmed" fw={600} size="xs">
          {t('Saved: {{time}}', { time: lastSavedAt.toLocaleTimeString() })}
        </Text>
      )}
      {isSaving ? (
        <Loader size={16} />
      ) : (
        <FiCheckCircle color="var(--mantine-color-success)" size={16} />
      )}
    </Flex>
  );
};

export default AutoSaveStatus;
